import React from 'react';
import {
    Checkbox,
    CheckboxGroup,
    Flex,
    FormControl,
    FormErrorMessage,
    FormHelperText,
    FormLabel,
    Input,
    Radio,
    RadioGroup,
    Select,
    Stack,
    Text,
    Textarea
} from '@chakra-ui/react';
import { Controller, Path, useFormContext } from 'react-hook-form';
import { useTranslate } from 'ts/common/hooks';

const TEXT_SHORT_MAX_LENGTH = 500;
const TEXT_LONG_MAX_LENGTH = 3000;

/**
 * Form values keyed by page index, then question id (string keys for react-hook-form path inference).
 * Text/dropdown/radio: string; checkboxes: string[] (choice ids).
 */
export type QuestionnaireFormValues = {
    pages: Record<string, Record<string, string | string[]>>;
};

interface Props {
    questionnairePage: SpApi.Client.IQuestionnairePage;
    pageIndex: number;
}

const QuestionnaireQuestionsForm: React.FC<Props> = ({ questionnairePage, pageIndex }) => {
    const t = useTranslate('clientQuestionnaire');
    const {
        register,
        control,
        watch,
        formState: { errors }
    } = useFormContext<QuestionnaireFormValues>();

    return (
        <Flex flexDirection="column" marginY="16px" gap="16px">
            <Text as="h3" fontSize="18px" fontWeight="600">
                {questionnairePage.name}
            </Text>
            {[...questionnairePage.questions]
                .sort((a, b) => a.displayOrder - b.displayOrder)
                .map((question) => {
                    const fieldName =
                        `pages.${pageIndex}.${question.id}` as Path<QuestionnaireFormValues>;
                    const fieldError = errors?.pages?.[String(pageIndex)]?.[String(question.id)];

                    return (
                        <FormControl
                            key={question.id}
                            isRequired={question.required}
                            isInvalid={!!fieldError}
                            paddingX={{ base: '16px', lg: '40px' }}
                            paddingY="24px"
                            boxSizing="border-box"
                        >
                            {question.responseType === 'choices_radio' ||
                            question.responseType === 'choices_checkboxes' ? (
                                <Text fontSize="16px" fontWeight="600" marginBottom="16px">
                                    {question.questionText}
                                </Text>
                            ) : (
                                <FormLabel fontSize="16px" fontWeight="600" marginBottom="16px">
                                    {question.questionText}
                                </FormLabel>
                            )}
                            {question.responseType === 'text_short' && (
                                <>
                                    <Input
                                        type="text"
                                        placeholder={question.responsePlaceholder ?? undefined}
                                        maxLength={TEXT_SHORT_MAX_LENGTH}
                                        {...register(fieldName, {
                                            required: question.required
                                                ? t('fieldRequired')
                                                : false,
                                            maxLength: {
                                                value: TEXT_SHORT_MAX_LENGTH,
                                                message: t('maxCharactersExceeded', {
                                                    max: TEXT_SHORT_MAX_LENGTH
                                                })
                                            }
                                        })}
                                    />
                                    <FormHelperText fontSize="xs">
                                        {t('characterCount', {
                                            current:
                                                (watch(fieldName) as unknown as string)?.length ??
                                                0,
                                            max: TEXT_SHORT_MAX_LENGTH
                                        })}
                                    </FormHelperText>
                                </>
                            )}
                            {question.responseType === 'text_long' && (
                                <>
                                    <Textarea
                                        variant="outline"
                                        placeholder={question.responsePlaceholder ?? undefined}
                                        minHeight="120px"
                                        maxLength={TEXT_LONG_MAX_LENGTH}
                                        {...register(fieldName, {
                                            required: question.required
                                                ? t('fieldRequired')
                                                : false,
                                            maxLength: {
                                                value: TEXT_LONG_MAX_LENGTH,
                                                message: t('maxCharactersExceeded', {
                                                    max: TEXT_LONG_MAX_LENGTH
                                                })
                                            }
                                        })}
                                    />
                                    <FormHelperText fontSize="xs">
                                        {t('characterCount', {
                                            current:
                                                (watch(fieldName) as unknown as string)?.length ??
                                                0,
                                            max: TEXT_LONG_MAX_LENGTH
                                        })}
                                    </FormHelperText>
                                </>
                            )}
                            {question.responseType === 'choices_dropdown' && (
                                <Controller
                                    name={fieldName}
                                    control={control}
                                    rules={{
                                        required: question.required ? t('fieldRequired') : false
                                    }}
                                    render={({ field }) => (
                                        <Select
                                            ref={field.ref}
                                            placeholder={question.responsePlaceholder ?? undefined}
                                            value={(field.value as unknown as string) ?? ''}
                                            name={fieldName}
                                            onBlur={field.onBlur}
                                            onChange={(e) => field.onChange(e.target.value)}
                                            maxWidth="400px"
                                        >
                                            {question.choices.map((choice) => (
                                                <option key={choice.id} value={choice.id}>
                                                    {choice.choiceText}
                                                </option>
                                            ))}
                                        </Select>
                                    )}
                                />
                            )}
                            {question.responseType === 'choices_radio' && (
                                <Controller
                                    name={fieldName}
                                    control={control}
                                    rules={{
                                        required: question.required ? t('fieldRequired') : false
                                    }}
                                    render={({ field }) => (
                                        <RadioGroup
                                            value={(field.value as unknown as string) ?? ''}
                                            onChange={field.onChange}
                                            aria-label={question.questionText}
                                        >
                                            <Stack direction="column" spacing={2}>
                                                {question.choices.map((choice) => (
                                                    <Radio
                                                        key={choice.id}
                                                        value={String(choice.id)}
                                                        name={fieldName}
                                                        onBlur={field.onBlur}
                                                    >
                                                        {choice.choiceText}
                                                    </Radio>
                                                ))}
                                            </Stack>
                                        </RadioGroup>
                                    )}
                                />
                            )}
                            {question.responseType === 'choices_checkboxes' && (
                                <Controller
                                    name={fieldName}
                                    control={control}
                                    rules={{
                                        validate: (value) =>
                                            !question.required ||
                                            (Array.isArray(value) && value.length > 0) ||
                                            t('atLeastOneOptionRequired')
                                    }}
                                    render={({ field }) => (
                                        <CheckboxGroup
                                            value={(field.value as unknown as string[]) ?? []}
                                            onChange={(values) => field.onChange(values)}
                                            aria-label={question.questionText}
                                        >
                                            <Stack direction="column" spacing={2}>
                                                {question.choices.map((choice) => (
                                                    <Checkbox
                                                        key={choice.id}
                                                        value={String(choice.id)}
                                                        name={fieldName}
                                                        onBlur={field.onBlur}
                                                    >
                                                        {choice.choiceText}
                                                    </Checkbox>
                                                ))}
                                            </Stack>
                                        </CheckboxGroup>
                                    )}
                                />
                            )}
                            {fieldError?.message && (
                                <FormErrorMessage>{fieldError.message as string}</FormErrorMessage>
                            )}
                        </FormControl>
                    );
                })}
        </Flex>
    );
};

export default QuestionnaireQuestionsForm;
